import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'wouter';
import { Layout } from '@/components/layout';
import { fmtDateTime } from '@/lib/dateFormat';
import { useAuthStore } from '@/lib/auth-store';
import { useToast } from '@/hooks/use-toast';
import { ShopifyTargetStoreSelector } from '@/components/shopify-target-store';
import { cn } from '@/lib/utils';
import {
  Truck, Eye, EyeOff, Save, Trash2, Loader2, CheckCircle2,
  AlertCircle, TestTube, ShieldCheck, Clock, Play, RefreshCw,
  ArrowRight, Columns, Hash, Package, ChevronDown, ChevronRight, KeyRound,
  History, TrendingUp, TrendingDown, Minus, XCircle, Download,
  FileSpreadsheet, Search, ShoppingBag, LayoutGrid, List as ListIcon, Image as ImageIcon,
} from 'lucide-react';

const BASE = import.meta.env.BASE_URL.replace(/\/$/, '');

interface OriStatus {
  configured: boolean;
  connectionType?: 'scraper' | 'api';
  username?: string | null;
  apiUrlMasked?: string | null;
  storeId?: string | null;
  scheduleEnabled?: boolean;
  scheduleInterval?: string;
  scheduleTime?: string;
  scheduleTimezone?: string;
  nextRunAt?: string | null;
  lastRunAt?: string | null;
  lastRunStatus?: 'success' | 'error' | null;
  lastRunError?: string | null;
  columnMapping?: Record<string, string> | null;
  csvHeaders?: string[] | null;
  updatedAt?: string;
}

interface StoreOption {
  id: string;
  nickname: string;
  storeUrl: string;
  status: string;
}

const SCHEDULE_OPTIONS = [
  { value: 'disabled', label: 'Disabled (manual only)' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
];

const TIMEZONE_OPTIONS = [
  { value: 'Australia/Melbourne', label: 'Melbourne / Sydney (AEST/AEDT)' },
  { value: 'Australia/Brisbane', label: 'Brisbane (AEST, no DST)' },
  { value: 'Australia/Adelaide', label: 'Adelaide (ACST/ACDT)' },
  { value: 'Australia/Darwin', label: 'Darwin (ACST, no DST)' },
  { value: 'Australia/Perth', label: 'Perth (AWST)' },
  { value: 'Pacific/Auckland', label: 'Auckland (NZST/NZDT)' },
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'New York (ET)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (PT)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
];

const inputCls =
  'w-full bg-muted/50 border border-border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 placeholder:text-slate-600';

const selectCls =
  'w-full h-9 appearance-none rounded-xl border border-border bg-muted/50 pl-3 pr-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary/50 transition-all';

function formatDate(d: string | null | undefined): string {
  return fmtDateTime(d);
}

export function OffroadImportsSettings() {
  const { token } = useAuthStore();
  const { toast } = useToast();
  const [location] = useLocation();
  const section: 'sync' | 'import' = location.startsWith('/offroad-imports/import') ? 'import' : 'sync';

  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  const [status, setStatus] = useState<OriStatus | null>(null);
  const [loadingStatus, setLoadingStatus] = useState(true);

  const [stores, setStores] = useState<StoreOption[]>([]);
  const [loadingStores, setLoadingStores] = useState(true);

  // ── Schedule & Store (editable without credentials) ────────────────────────
  const [scheduleOption, setScheduleOption] = useState<string>('disabled');
  const [scheduleTime, setScheduleTime] = useState<string>('09:00');
  const [scheduleTimezone, setScheduleTimezone] = useState<string>('Australia/Melbourne');
  const [selectedStoreId, setSelectedStoreId] = useState<string>('');
  const [savingSettings, setSavingSettings] = useState(false);

  // ── Credentials (for first-time connect or password change) ────────────────
  const [showCredentialUpdate, setShowCredentialUpdate] = useState(false);
  // Connection type for the *form* (may differ from saved status while editing)
  const [formConnectionType, setFormConnectionType] = useState<'scraper' | 'api'>('scraper');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [apiUrl, setApiUrl] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{
    ok: boolean; message?: string; error?: string;
    csvHeaders?: string[]; csvSampleRows?: Record<string, string>[];
  } | null>(null);

  // ── Column mapping ─────────────────────────────────────────────────────────
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [skuColumn, setSkuColumn] = useState<string>('Code');
  const [qtyColumn, setQtyColumn] = useState<string>('Availability');
  const [descriptionColumn, setDescriptionColumn] = useState<string>('');
  const [savingMapping, setSavingMapping] = useState(false);
  const [csvSampleRows, setCsvSampleRows] = useState<Record<string, string>[]>([]);

  // ── Price sync column mapping ───────────────────────────────────────────────
  const [priceSkuColumn, setPriceSkuColumn] = useState<string>('Code');
  const [priceColumn, setPriceColumn] = useState<string>('');
  const [savingPriceMapping, setSavingPriceMapping] = useState(false);

  // ── Price sync live progress ────────────────────────────────────────────────
  const [priceRunning, setPriceRunning] = useState(false);
  const [priceCancelling, setPriceCancelling] = useState(false);
  const [priceRunningJobId, setPriceRunningJobId] = useState<string | null>(null);
  const [priceJobProgress, setPriceJobProgress] = useState<OriJobProgress | null>(null);

  // ── Run logs ───────────────────────────────────────────────────────────────
  interface OriSyncDetail {
    sku: string;
    oldQty: number | null;
    newQty: number;
    status: 'updated' | 'error' | 'not_found';
    error?: string;
  }
  interface OriLog {
    id: string;
    filename: string;
    storeName: string;
    totalRows: number;
    successCount: number;
    skipCount: number;
    errorCount: number;
    status: string;
    createdAt: string;
    completedAt: string | null;
    details: OriSyncDetail[] | null;
  }
  const [logs, setLogs] = useState<OriLog[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);
  const [logFilter, setLogFilter] = useState<'all' | 'updated' | 'not_found' | 'error'>('all');
  const [exportingJobId, setExportingJobId] = useState<string | null>(null);

  // ── Live progress (polling) ─────────────────────────────────────────────────
  interface OriJobProgress {
    id: string;
    status: string;
    totalRows: number;
    processedCount: number;
    successCount: number;
    skipCount: number;
    errorCount: number;
    createdAt: string;
    completedAt: string | null;
  }
  const [runningJobId, setRunningJobId] = useState<string | null>(null);
  const [jobProgress, setJobProgress] = useState<OriJobProgress | null>(null);

  // ── Misc ───────────────────────────────────────────────────────────────────
  const [deleting, setDeleting] = useState(false);
  const [running, setRunning] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  // ── Product Catalog ────────────────────────────────────────────────────────
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [catalogHeaders, setCatalogHeaders] = useState<string[]>([]);
  const [catalogRows, setCatalogRows] = useState<Record<string, string>[]>([]);
  const [catalogColMap, setCatalogColMap] = useState<{
    title: string; description: string; sku: string; price: string; cost: string;
    brand: string; category: string; barcode: string; imageUrl: string;
    imageUrls: string[];
  }>({ title: '', description: '', sku: '', price: '', cost: '', brand: '', category: '', barcode: '', imageUrl: '', imageUrls: ['', '', '', '', ''] });
  const [catalogSearch, setCatalogSearch] = useState('');
  const [catalogTypeFilter, setCatalogTypeFilter] = useState('');
  const [catalogViewMode, setCatalogViewMode] = useState<'table' | 'tiles'>('tiles');
  const [catalogPage, setCatalogPage] = useState(0);
  const CATALOG_PAGE_SIZE = 500;
  const [catalogSelected, setCatalogSelected] = useState<Set<number>>(new Set());
  const [catalogImportStoreId, setCatalogImportStoreId] = useState('');
  const [catalogDupHandling, setCatalogDupHandling] = useState<'skip' | 'overwrite' | 'create'>('skip');
  const [catalogImporting, setCatalogImporting] = useState(false);
  const [catalogImportProgress, setCatalogImportProgress] = useState<{ current: number; total: number } | null>(null);
  const [catalogImportResult, setCatalogImportResult] = useState<{
    successCount: number; skipCount: number; errorCount: number;
    errors: { title: string; error: string }[];
  } | null>(null);

  // ── Update existing listings ───────────────────────────────────────────────
  const [updateFields, setUpdateFields] = useState<Set<string>>(new Set(['description']));
  const [updatingListings, setUpdatingListings] = useState(false);
  const [updateProgress, setUpdateProgress] = useState<{ phase: string; current: number; total: number } | null>(null);
  const [updateResult, setUpdateResult] = useState<{
    updatedCount: number; notFoundCount: number; errorCount: number;
    errors: { title: string; error: string }[];
  } | null>(null);

  async function loadStores() {
    setLoadingStores(true);
    try {
      const r = await fetch(`${BASE}/api/offroadimports/stores`, { headers: authHeaders });
      if (!r.ok) throw new Error(r.statusText);
      const data = await r.json();
      setStores(data.stores ?? []);
    } catch {
      setStores([]);
    } finally {
      setLoadingStores(false);
    }
  }

  async function loadStatus() {
    setLoadingStatus(true);
    try {
      const r = await fetch(`${BASE}/api/offroadimports/credentials`, { headers: authHeaders });
      if (!r.ok) throw new Error(r.statusText);
      const data: OriStatus = await r.json();
      setStatus(data);
      if (data.scheduleEnabled !== undefined) {
        setScheduleOption(data.scheduleEnabled ? (data.scheduleInterval ?? 'daily') : 'disabled');
      }
      if (data.scheduleTime) setScheduleTime(data.scheduleTime);
      if (data.scheduleTimezone) setScheduleTimezone(data.scheduleTimezone);
      if (data.storeId) setSelectedStoreId(data.storeId);
      if (data.connectionType) setFormConnectionType(data.connectionType);
      if (data.csvHeaders) setCsvHeaders(data.csvHeaders);
      if (data.columnMapping) {
        setSkuColumn(data.columnMapping.skuColumn ?? data.columnMapping.variant_sku ?? 'Code');
        setQtyColumn(data.columnMapping.qtyColumn ?? data.columnMapping.variant_inventory_qty ?? 'Availability');
        setDescriptionColumn(data.columnMapping.descriptionColumn ?? '');
        setPriceSkuColumn(data.columnMapping.priceSkuColumn ?? 'Code');
        setPriceColumn(data.columnMapping.priceColumn ?? '');
      }
    } catch {
      setStatus({ configured: false });
    } finally {
      setLoadingStatus(false);
    }
  }

  async function loadLogs() {
    setLoadingLogs(true);
    try {
      const r = await fetch(`${BASE}/api/offroadimports/logs?limit=10`, {
        headers: authHeaders,
        cache: 'no-store',
      });
      if (!r.ok) throw new Error(r.statusText);
      const data = await r.json();
      const jobs: typeof logs = data.logs ?? [];
      setLogs(jobs);

      // Restore running state if there's already a sync in progress
      // (e.g. page refreshed mid-sync, or sync was started from the scheduler).
      // Guards:
      //  1. Only 'running' status — never re-attach to 'cancelled' jobs.
      //  2. Only jobs started within the last 4 hours — a job stuck in 'running'
      //     beyond that was interrupted by a server restart; the API startup cleanup
      //     marks these as 'failed', but this guard prevents a brief re-attach window
      //     between server start and the first /logs poll returning the updated status.
      const STALE_REATTACH_THRESHOLD_MS = 4 * 60 * 60 * 1000; // 4 hours
      const inFlight = jobs.find((j) => {
        if (j.status !== 'running') return false;
        const ageMs = Date.now() - new Date(j.createdAt).getTime();
        return ageMs < STALE_REATTACH_THRESHOLD_MS;
      });
      if (inFlight && !runningJobId) {
        setRunningJobId(inFlight.id);
        setRunning(true);
        setJobProgress({
          id: inFlight.id,
          status: 'running',
          totalRows: inFlight.totalRows ?? 0,
          processedCount: -1, // processedCount not included in logs query; will update on first poll
          successCount: inFlight.successCount ?? 0,
          skipCount: inFlight.skipCount ?? 0,
          errorCount: inFlight.errorCount ?? 0,
          createdAt: inFlight.createdAt,
          completedAt: null,
        });
      }
    } catch {
      setLogs([]);
    } finally {
      setLoadingLogs(false);
    }
  }

  useEffect(() => {
    loadStatus();
    loadStores();
  }, []);

  // Load logs once we know the user is configured
  useEffect(() => {
    if (status?.configured) loadLogs();
  }, [status?.configured]);

  // When test succeeds, expose headers and sample rows for the mapper
  useEffect(() => {
    if (testResult?.ok) {
      if (testResult.csvHeaders && testResult.csvHeaders.length > 0) {
        setCsvHeaders(testResult.csvHeaders);
      }
      if (testResult.csvSampleRows && testResult.csvSampleRows.length > 0) {
        setCsvSampleRows(testResult.csvSampleRows);
      }
    }
  }, [testResult]);

  // ── Handlers ───────────────────────────────────────────────────────────────

  async function handleSaveSettings() {
    setSavingSettings(true);
    try {
      const scheduleEnabled = scheduleOption !== 'disabled';
      const scheduleInterval = scheduleEnabled ? scheduleOption : 'daily';
      const r = await fetch(`${BASE}/api/offroadimports/credentials/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ scheduleEnabled, scheduleInterval, scheduleTime, scheduleTimezone, storeId: selectedStoreId || null }),
      });
      if (!r.ok) throw new Error((await r.json()).error ?? r.statusText);
      toast({ title: 'Settings saved' });
      await loadStatus();
    } catch (e: any) {
      toast({ title: 'Save failed', description: e.message, variant: 'destructive' });
    } finally {
      setSavingSettings(false);
    }
  }

  async function handleTest() {
    if (formConnectionType === 'api') {
      if (!apiUrl.trim()) {
        toast({ title: 'Enter API URL first', description: 'Paste your Off Road Imports API link to test.', variant: 'destructive' });
        return;
      }
    } else {
      if (!username.trim() || !password.trim()) {
        toast({ title: 'Enter credentials first', description: 'Username and password are required to test.', variant: 'destructive' });
        return;
      }
    }
    setTesting(true);
    setTestResult(null);
    try {
      const body = formConnectionType === 'api'
        ? { connectionType: 'api', apiUrl: apiUrl.trim() }
        : { connectionType: 'scraper', username: username.trim(), password: password.trim() };
      const r = await fetch(`${BASE}/api/offroadimports/credentials/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify(body),
      });
      const data = await r.json();
      setTestResult(data);
    } catch (e: any) {
      setTestResult({ ok: false, error: e.message ?? 'Connection failed' });
    } finally {
      setTesting(false);
    }
  }

  async function handleSaveCredentials() {
    if (formConnectionType === 'api') {
      if (!apiUrl.trim()) {
        toast({ title: 'Missing API URL', description: 'Paste your Off Road Imports API link.', variant: 'destructive' });
        return;
      }
    } else {
      if (!username.trim() || !password.trim()) {
        toast({ title: 'Missing fields', description: 'Username and password are required.', variant: 'destructive' });
        return;
      }
    }
    setSaving(true);
    try {
      const scheduleEnabled = scheduleOption !== 'disabled';
      const scheduleInterval = scheduleEnabled ? scheduleOption : 'daily';
      const body = formConnectionType === 'api'
        ? { connectionType: 'api', apiUrl: apiUrl.trim(), scheduleEnabled, scheduleInterval, scheduleTime, scheduleTimezone, storeId: selectedStoreId || null }
        : { connectionType: 'scraper', username: username.trim(), password: password.trim(), scheduleEnabled, scheduleInterval, scheduleTime, scheduleTimezone, storeId: selectedStoreId || null };
      const r = await fetch(`${BASE}/api/offroadimports/credentials`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error((await r.json()).error ?? r.statusText);
      toast({ title: status?.configured ? 'Connection updated' : 'Account connected', description: 'Off Road Imports connection saved.' });
      setUsername('');
      setPassword('');
      setApiUrl('');
      setTestResult(null);
      setShowCredentialUpdate(false);
      await loadStatus();
    } catch (e: any) {
      toast({ title: 'Save failed', description: e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveMapping() {
    if (!status?.configured) return;
    setSavingMapping(true);
    try {
      const r = await fetch(`${BASE}/api/offroadimports/credentials/column-mapping`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          mapping: { skuColumn, qtyColumn, descriptionColumn, priceSkuColumn, priceColumn },
          headers: csvHeaders,
        }),
      });
      if (!r.ok) throw new Error((await r.json()).error ?? r.statusText);
      toast({ title: 'Column mapping saved', description: 'The sync will use this mapping automatically.' });
      await loadStatus();
    } catch (e: any) {
      toast({ title: 'Failed to save mapping', description: e.message, variant: 'destructive' });
    } finally {
      setSavingMapping(false);
    }
  }

  async function handleSavePriceMapping() {
    if (!status?.configured) return;
    setSavingPriceMapping(true);
    try {
      const r = await fetch(`${BASE}/api/offroadimports/credentials/column-mapping`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          mapping: { skuColumn, qtyColumn, descriptionColumn, priceSkuColumn, priceColumn },
        }),
      });
      if (!r.ok) throw new Error((await r.json()).error ?? r.statusText);
      toast({ title: 'Price sync columns saved' });
      await loadStatus();
    } catch (e: any) {
      toast({ title: 'Failed to save', description: e.message, variant: 'destructive' });
    } finally {
      setSavingPriceMapping(false);
    }
  }

  async function handleDelete() {
    if (!confirm('Remove your Off Road Imports credentials? Scheduled syncs will stop.')) return;
    setDeleting(true);
    try {
      await fetch(`${BASE}/api/offroadimports/credentials`, { method: 'DELETE', headers: authHeaders });
      toast({ title: 'Credentials removed' });
      setStatus({ configured: false });
      setScheduleOption('disabled');
      setScheduleTime('09:00');
      setScheduleTimezone('Australia/Melbourne');
      setSelectedStoreId('');
      setCsvHeaders([]);
      setSkuColumn('Code');
      setQtyColumn('Availability');
      setDescriptionColumn('');
      setCsvSampleRows([]);
      setPriceSkuColumn('Code');
      setPriceColumn('');
      setShowCredentialUpdate(false);
      setFormConnectionType('scraper');
      setApiUrl('');
    } catch (e: any) {
      toast({ title: 'Failed to remove', description: e.message, variant: 'destructive' });
    } finally {
      setDeleting(false);
    }
  }

  // Poll a job by ID — called by the useEffect below every 2 s
  async function pollJob(jobId: string) {
    try {
      const r = await fetch(`${BASE}/api/offroadimports/jobs/${jobId}`, { headers: authHeaders });
      if (!r.ok) return;
      const data: OriJobProgress = await r.json();
      setJobProgress(data);
      if (data.status === 'done' || data.status === 'failed' || data.status === 'cancelled') {
        setRunningJobId(null);
        setRunning(false);
        setCancelling(false);
        loadLogs(); // refresh the Run History list
      }
    } catch {}
  }

  async function handleCancel() {
    if (!runningJobId) return;
    setCancelling(true);
    try {
      const r = await fetch(`${BASE}/api/offroadimports/jobs/${runningJobId}/cancel`, {
        method: 'POST',
        headers: authHeaders,
      });
      if (!r.ok) {
        const data = await r.json();
        toast({ title: 'Cancel failed', description: data.error ?? r.statusText, variant: 'destructive' });
        setCancelling(false);
      }
      // On success: the poll loop will pick up the 'cancelled' status within 2 s
    } catch (e: any) {
      toast({ title: 'Cancel failed', description: e.message, variant: 'destructive' });
      setCancelling(false);
    }
  }

  // Start polling whenever runningJobId is set
  useEffect(() => {
    if (!runningJobId) return;
    const id = setInterval(() => pollJob(runningJobId), 2000);
    return () => clearInterval(id);
  }, [runningJobId]);

  // ── Price sync handlers ───────────────────────────────────────────────────
  async function pollPriceJob(jobId: string) {
    try {
      const r = await fetch(`${BASE}/api/offroadimports/jobs/${jobId}`, { headers: authHeaders });
      if (!r.ok) return;
      const data: OriJobProgress = await r.json();
      setPriceJobProgress(data);
      if (data.status === 'done' || data.status === 'failed' || data.status === 'cancelled') {
        setPriceRunningJobId(null);
        setPriceRunning(false);
        setPriceCancelling(false);
        loadLogs();
      }
    } catch {}
  }

  useEffect(() => {
    if (!priceRunningJobId) return;
    const id = setInterval(() => pollPriceJob(priceRunningJobId), 2000);
    return () => clearInterval(id);
  }, [priceRunningJobId]);

  async function handleRunPriceSync() {
    setPriceRunning(true);
    setPriceJobProgress(null);
    try {
      const r = await fetch(`${BASE}/api/offroadimports/sync/price`, { method: 'POST', headers: authHeaders });
      const data = await r.json();
      if (r.status === 409 && data.alreadyRunning) {
        if (data.jobId) {
          setPriceRunningJobId(data.jobId);
          setPriceJobProgress({ id: data.jobId, status: 'running', totalRows: 0, processedCount: -1, successCount: 0, skipCount: 0, errorCount: 0, createdAt: new Date().toISOString(), completedAt: null });
        } else {
          toast({ title: 'Sync already running', description: 'A sync is already in progress.', variant: 'default' });
          setPriceRunning(false);
        }
        return;
      }
      if (!r.ok) throw new Error(data.error ?? r.statusText);
      if (data.jobId) {
        setPriceRunningJobId(data.jobId);
        setPriceJobProgress({ id: data.jobId, status: 'running', totalRows: 0, processedCount: -1, successCount: 0, skipCount: 0, errorCount: 0, createdAt: new Date().toISOString(), completedAt: null });
      } else {
        toast({ title: 'Price sync could not start', description: 'Check your credentials and store connection.', variant: 'destructive' });
        setPriceRunning(false);
      }
    } catch (e: any) {
      toast({ title: 'Price sync failed to start', description: e.message, variant: 'destructive' });
      setPriceRunning(false);
    }
  }

  async function handlePriceCancel() {
    if (!priceRunningJobId) return;
    setPriceCancelling(true);
    try {
      const r = await fetch(`${BASE}/api/offroadimports/jobs/${priceRunningJobId}/cancel`, {
        method: 'POST',
        headers: authHeaders,
      });
      if (!r.ok) {
        const data = await r.json();
        toast({ title: 'Cancel failed', description: data.error ?? r.statusText, variant: 'destructive' });
        setPriceCancelling(false);
      }
    } catch (e: any) {
      toast({ title: 'Cancel failed', description: e.message, variant: 'destructive' });
      setPriceCancelling(false);
    }
  }

  async function handleRunNow() {
    setRunning(true);
    setJobProgress(null);
    try {
      const r = await fetch(`${BASE}/api/offroadimports/sync/run`, { method: 'POST', headers: authHeaders });
      const data = await r.json();

      // 409 = a sync is already running for this account — attach to it
      if (r.status === 409 && data.alreadyRunning) {
        if (data.jobId) {
          setRunningJobId(data.jobId);
          setJobProgress({ id: data.jobId, status: 'running', totalRows: 0, processedCount: -1, successCount: 0, skipCount: 0, errorCount: 0, createdAt: new Date().toISOString(), completedAt: null });
        } else {
          toast({ title: 'Sync already running', description: 'A sync is already in progress for this account.', variant: 'default' });
          setRunning(false);
        }
        return;
      }

      if (!r.ok) throw new Error(data.error ?? r.statusText);
      if (data.jobId) {
        setRunningJobId(data.jobId);
        // seed initial progress so panel appears immediately
        setJobProgress({ id: data.jobId, status: 'running', totalRows: 0, processedCount: -1, successCount: 0, skipCount: 0, errorCount: 0, createdAt: new Date().toISOString(), completedAt: null });
      } else {
        // Server responded but couldn't create a job (credentials/store issue)
        toast({ title: 'Sync could not start', description: 'Check your credentials and store connection.', variant: 'destructive' });
        setRunning(false);
      }
    } catch (e: any) {
      toast({ title: 'Sync failed to start', description: e.message, variant: 'destructive' });
      setRunning(false);
    }
  }

  async function exportCsv(jobId: string, createdAt: string) {
    setExportingJobId(jobId);
    try {
      const r = await fetch(`${BASE}/api/offroadimports/jobs/${jobId}/export.csv`, { headers: authHeaders });
      if (!r.ok) throw new Error(await r.text());
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ori-sync-${createdAt.slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e: any) {
      toast({ title: 'Export failed', description: e.message, variant: 'destructive' });
    } finally {
      setExportingJobId(null);
    }
  }

  // ── Catalog derived state ──────────────────────────────────────────────────
  const catalogTypeOptions = useMemo(() => {
    if (!catalogColMap.category) return [];
    const set = new Set<string>();
    catalogRows.forEach(row => {
      const v = (row[catalogColMap.category] ?? '').trim();
      if (v) set.add(v);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [catalogRows, catalogColMap.category]);

  const filteredCatalog = useMemo(() => {
    const q = catalogSearch.trim().toLowerCase();
    return catalogRows
      .map((row, absIdx) => ({ row, absIdx }))
      .filter(({ row }) => !q || Object.values(row).some(v => v.toLowerCase().includes(q)))
      .filter(({ row }) => !catalogTypeFilter || row[catalogColMap.category] === catalogTypeFilter);
  }, [catalogRows, catalogSearch, catalogTypeFilter, catalogColMap.category]);

  const catalogTotalPages = Math.max(1, Math.ceil(filteredCatalog.length / CATALOG_PAGE_SIZE));
  useEffect(() => {
    if (catalogPage > catalogTotalPages - 1) setCatalogPage(0);
  }, [catalogTotalPages, catalogPage]);
  const paginatedCatalog = useMemo(() => {
    const start = catalogPage * CATALOG_PAGE_SIZE;
    return filteredCatalog.slice(start, start + CATALOG_PAGE_SIZE);
  }, [filteredCatalog, catalogPage]);

  function getRowImageUrl(row: Record<string, string>): string {
    for (const col of catalogColMap.imageUrls) {
      if (col && row[col]?.trim()) return row[col].trim();
    }
    if (catalogColMap.imageUrl && row[catalogColMap.imageUrl]?.trim()) return row[catalogColMap.imageUrl].trim();
    return '';
  }

  const catalogHasImageMapping = !!catalogColMap.imageUrl || catalogColMap.imageUrls.some(Boolean);

  async function handleFetchCatalog() {
    setCatalogLoading(true);
    setCatalogError(null);
    setCatalogRows([]);
    setCatalogHeaders([]);
    setCatalogSelected(new Set());
    setCatalogImportResult(null);
    setCatalogTypeFilter('');
    try {
      const r = await fetch(`${BASE}/api/offroadimports/catalog/fetch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({}),
      });
      const data = await r.json();
      if (!data.ok) throw new Error(data.error ?? 'Fetch failed');
      setCatalogHeaders(data.headers ?? []);
      setCatalogRows(data.rows ?? []);
      setCatalogColMap({
        title:       data.detected?.title       ?? '',
        description: data.detected?.description ?? '',
        sku:         data.detected?.sku         ?? '',
        price:       data.detected?.price       ?? '',
        cost:        data.detected?.cost        ?? '',
        brand:       data.detected?.brand       ?? '',
        category:    data.detected?.category    ?? '',
        barcode:     data.detected?.barcode     ?? '',
        imageUrl:    data.detected?.imageUrl    ?? '',
        imageUrls: (() => {
          const detected: string[] = Array.isArray(data.detected?.imageUrls) ? data.detected.imageUrls : [];
          const padded = [...detected];
          while (padded.length < 5) padded.push('');
          return padded.slice(0, 5);
        })(),
      });
      if (selectedStoreId) setCatalogImportStoreId(selectedStoreId);
      toast({ title: 'Catalogue loaded', description: `${data.totalRows} products found.` });
    } catch (e: any) {
      setCatalogError(e.message);
      toast({ title: 'Catalogue fetch failed', description: e.message, variant: 'destructive' });
    } finally {
      setCatalogLoading(false);
    }
  }

  async function handleCatalogImport() {
    if (catalogSelected.size === 0) {
      toast({ title: 'No products selected', variant: 'destructive' }); return;
    }
    if (!catalogImportStoreId) {
      toast({ title: 'Select a target store first', variant: 'destructive' }); return;
    }
    const rowsToImport = catalogRows.filter((_, i) => catalogSelected.has(i));
    setCatalogImporting(true);
    setCatalogImportProgress({ current: 0, total: rowsToImport.length });
    setCatalogImportResult(null);
    try {
      const r = await fetch(`${BASE}/api/offroadimports/catalog/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          storeId: catalogImportStoreId,
          rows: rowsToImport,
          mapping: catalogColMap,
          duplicateHandling: catalogDupHandling,
        }),
      });
      const reader = r.body!.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split('\n');
        buf = lines.pop() ?? '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const ev = JSON.parse(line.slice(6));
            if (ev.type === 'progress') {
              setCatalogImportProgress({ current: ev.current, total: ev.total });
            } else if (ev.type === 'done') {
              setCatalogImportResult({
                successCount: ev.successCount,
                skipCount: ev.skipCount,
                errorCount: ev.errorCount,
                errors: ev.errors ?? [],
              });
              toast({ title: 'Import complete', description: `${ev.successCount} products created.` });
            } else if (ev.type === 'error') {
              throw new Error(ev.message);
            }
          } catch { /* malformed line */ }
        }
      }
    } catch (e: any) {
      setCatalogError(e.message);
      toast({ title: 'Import failed', description: e.message, variant: 'destructive' });
    } finally {
      setCatalogImporting(false);
      setCatalogImportProgress(null);
    }
  }

  async function handleUpdateListings() {
    if (updateFields.size === 0) {
      toast({ title: 'Select at least one field to update', variant: 'destructive' }); return;
    }
    if (!catalogImportStoreId) {
      toast({ title: 'Select a target store first', variant: 'destructive' }); return;
    }
    const rowsToSend = catalogSelected.size > 0
      ? catalogRows.filter((_, i) => catalogSelected.has(i))
      : catalogRows;
    if (rowsToSend.length === 0) {
      toast({ title: 'No rows to update', variant: 'destructive' }); return;
    }

    // Build mapping object from catalogColMap + updateFields
    const mapping: Record<string, string> = { sku: catalogColMap.sku };
    if (updateFields.has('description') && catalogColMap.description) mapping.description = catalogColMap.description;
    if (updateFields.has('title')       && catalogColMap.title)        mapping.title       = catalogColMap.title;
    if (updateFields.has('price')       && catalogColMap.price)    mapping.price       = catalogColMap.price;
    if (updateFields.has('brand')       && catalogColMap.brand)    mapping.brand       = catalogColMap.brand;
    if (updateFields.has('category')    && catalogColMap.category) mapping.category    = catalogColMap.category;

    setUpdatingListings(true);
    setUpdateProgress({ phase: 'building_map', current: 0, total: rowsToSend.length });
    setUpdateResult(null);

    try {
      const r = await fetch(`${BASE}/api/offroadimports/catalog/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          storeId: catalogImportStoreId,
          rows: rowsToSend,
          mapping,
          fields: Array.from(updateFields),
        }),
      });
      if (!r.ok || !r.body) throw new Error('Update stream failed to start');
      const reader = r.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split('\n');
        buf = lines.pop() ?? '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const ev = JSON.parse(line.slice(6));
            if (ev.type === 'progress') {
              setUpdateProgress({ phase: ev.phase ?? 'updating', current: ev.current, total: ev.total });
            } else if (ev.type === 'done') {
              setUpdateResult({ updatedCount: ev.updatedCount, notFoundCount: ev.notFoundCount, errorCount: ev.errorCount, errors: ev.errors ?? [] });
              toast({ title: 'Update complete', description: `${ev.updatedCount} listing${ev.updatedCount !== 1 ? 's' : ''} updated.` });
            } else if (ev.type === 'error') {
              throw new Error(ev.message);
            }
          } catch { /* malformed line */ }
        }
      }
    } catch (e: any) {
      toast({ title: 'Update failed', description: e.message, variant: 'destructive' });
    } finally {
      setUpdatingListings(false);
      setUpdateProgress(null);
    }
  }

  const hasSavedMapping = status?.configured &&
    status.columnMapping &&
    (status.columnMapping.skuColumn || status.columnMapping.variant_sku);

  const hasSavedPriceMapping = status?.configured &&
    status.columnMapping?.priceSkuColumn &&
    status.columnMapping?.priceColumn;

  const mappingHasRequired = !!skuColumn && !!qtyColumn;
  const showColumnMapper = status?.configured && csvHeaders.length > 0;
  const isPriceSyncAvailable = status?.connectionType === 'scraper';

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Layout>
      <div className="space-y-6 max-w-2xl">

        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
            <Truck className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-display font-bold text-gradient">
              Off Road Imports — {section === 'import' ? 'Import' : 'Scheduled Sync'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {section === 'import'
                ? 'Download the ORI product catalogue and import products to Shopify.'
                : 'Automatically sync inventory quantities from offroadimports.com.au to Shopify on a schedule.'}
            </p>
          </div>
        </div>

        {/* Sub-nav tabs */}
        <div className="flex items-center gap-1 border-b border-border/50 pb-0">
          <Link
            href="/offroad-imports/sync"
            className={cn(
              "px-4 py-2 text-sm border-b-2 -mb-px transition-colors",
              section === 'sync'
                ? "border-primary text-primary font-medium"
                : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            Scheduled Sync
          </Link>
          <Link
            href="/offroad-imports/import"
            className={cn(
              "px-4 py-2 text-sm border-b-2 -mb-px transition-colors",
              section === 'import'
                ? "border-primary text-primary font-medium"
                : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            Import
          </Link>
        </div>

        {section === 'sync' && (<>
        {/* ── Status card ── */}
        {loadingStatus ? (
          <div className="glass-panel rounded-xl p-5 flex items-center gap-3 text-muted-foreground text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Checking connection status…
          </div>
        ) : status?.configured ? (
          <div className="glass-panel rounded-xl p-5 space-y-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                <span className="text-sm font-medium text-emerald-400">Connected</span>
              </div>
              <div className="flex flex-col items-end gap-1.5">
                <div className="flex items-center gap-2">
                  {/* Inventory sync button */}
                  <button
                    onClick={handleRunNow}
                    disabled={running || priceRunning || !hasSavedMapping}
                    title={!hasSavedMapping ? 'Save your column mapping before running a sync' : undefined}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/10 hover:bg-primary/20 border border-primary/20 text-xs text-primary transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                    Stock Sync
                  </button>
                  {/* Price-only sync button */}
                  <button
                    onClick={handleRunPriceSync}
                    disabled={priceRunning || running || !hasSavedPriceMapping || !isPriceSyncAvailable}
                    title={
                      !isPriceSyncAvailable ? 'Price sync requires username/password credentials (not API key)' :
                      !hasSavedPriceMapping ? 'Configure price sync columns below first' :
                      undefined
                    }
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/20 text-xs text-emerald-400 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {priceRunning ? <Loader2 className="w-3 h-3 animate-spin" /> : <TrendingUp className="w-3 h-3" />}
                    Price Sync
                  </button>
                </div>
                {!hasSavedMapping && (
                  <span className="text-[10px] text-amber-400/80 text-right">Set column mapping first</span>
                )}
                {hasSavedMapping && !hasSavedPriceMapping && isPriceSyncAvailable && (
                  <span className="text-[10px] text-slate-500 text-right">Set price columns below to enable Price Sync</span>
                )}
              </div>
            </div>

            {/* ── Live progress panel ── */}
            {jobProgress && (
              <div className={`rounded-xl border px-4 py-3 space-y-2.5 transition-all ${
                jobProgress.status === 'done' ? 'border-emerald-500/30 bg-emerald-500/5'
                : jobProgress.status === 'failed' ? 'border-red-500/30 bg-red-500/5'
                : jobProgress.status === 'cancelled' ? 'border-amber-500/30 bg-amber-500/5'
                : 'border-primary/30 bg-primary/5'
              }`}>
                {/* Phase label + spinner + cancel button */}
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {jobProgress.status === 'done' ? (
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                    ) : jobProgress.status === 'failed' ? (
                      <XCircle className="w-3.5 h-3.5 text-red-400 shrink-0" />
                    ) : jobProgress.status === 'cancelled' ? (
                      <XCircle className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                    ) : (
                      <Loader2 className="w-3.5 h-3.5 text-primary animate-spin shrink-0" />
                    )}
                    <span className="text-xs font-medium text-foreground truncate">
                      {jobProgress.status === 'done' ? 'Sync complete' :
                       jobProgress.status === 'failed' ? 'Sync failed' :
                       jobProgress.status === 'cancelled' ? 'Sync cancelled' :
                       jobProgress.processedCount === -1 ? 'Downloading CSV from Off Road Imports…' :
                       jobProgress.processedCount === -2 ? 'Building Shopify product & SKU map…' :
                       jobProgress.processedCount === -3 ? 'Fetching current inventory levels…' :
                       jobProgress.totalRows > 0 ? `Updating inventory… (${jobProgress.processedCount} / ${jobProgress.totalRows} rows)` :
                       'Starting…'}
                    </span>
                  </div>
                  {/* Cancel button — only while sync is running */}
                  {jobProgress.status === 'running' && (
                    <button
                      onClick={handleCancel}
                      disabled={cancelling}
                      className="shrink-0 flex items-center gap-1 px-2.5 py-1 rounded-lg border border-red-500/30 bg-red-500/10 hover:bg-red-500/20 text-[11px] text-red-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {cancelling ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />}
                      {cancelling ? 'Cancelling…' : 'Cancel'}
                    </button>
                  )}
                </div>

                {/* Progress bar — only when we're on row-by-row updates */}
                {jobProgress.totalRows > 0 && jobProgress.processedCount >= 0 && (
                  <div className="space-y-1">
                    <div className="w-full bg-muted/50 rounded-full h-1.5 overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${
                          jobProgress.status === 'done' ? 'bg-emerald-400'
                          : jobProgress.status === 'failed' ? 'bg-red-400'
                          : 'bg-primary'
                        }`}
                        style={{ width: `${Math.min(100, (jobProgress.processedCount / jobProgress.totalRows) * 100)}%` }}
                      />
                    </div>
                    <div className="text-[10px] text-slate-600 text-right">
                      {Math.round((jobProgress.processedCount / jobProgress.totalRows) * 100)}%
                    </div>
                  </div>
                )}

                {/* Live counters */}
                {(jobProgress.successCount > 0 || jobProgress.skipCount > 0 || jobProgress.errorCount > 0) && (
                  <div className="flex items-center gap-4 text-[11px]">
                    {jobProgress.successCount > 0 && (
                      <span className="text-emerald-400">{jobProgress.successCount} updated</span>
                    )}
                    {jobProgress.skipCount > 0 && (
                      <span className="text-muted-foreground">{jobProgress.skipCount} not in Shopify</span>
                    )}
                    {jobProgress.errorCount > 0 && (
                      <span className="text-red-400">{jobProgress.errorCount} errors</span>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ── Price sync progress panel ── */}
            {priceJobProgress && (
              <div className={`rounded-xl border px-4 py-3 space-y-2.5 transition-all ${
                priceJobProgress.status === 'done' ? 'border-emerald-500/30 bg-emerald-500/5'
                : priceJobProgress.status === 'failed' ? 'border-red-500/30 bg-red-500/5'
                : priceJobProgress.status === 'cancelled' ? 'border-amber-500/30 bg-amber-500/5'
                : 'border-emerald-500/20 bg-emerald-500/5'
              }`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {priceJobProgress.status === 'done' ? (
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                    ) : priceJobProgress.status === 'failed' ? (
                      <XCircle className="w-3.5 h-3.5 text-red-400 shrink-0" />
                    ) : priceJobProgress.status === 'cancelled' ? (
                      <XCircle className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                    ) : (
                      <Loader2 className="w-3.5 h-3.5 text-emerald-400 animate-spin shrink-0" />
                    )}
                    <span className="text-xs font-medium text-foreground truncate">
                      {priceJobProgress.status === 'done' ? 'Price sync complete' :
                       priceJobProgress.status === 'failed' ? 'Price sync failed' :
                       priceJobProgress.status === 'cancelled' ? 'Price sync cancelled' :
                       priceJobProgress.processedCount === -1 ? 'Downloading XLSX price list from ORI…' :
                       priceJobProgress.processedCount === -2 ? 'Building Shopify variant map…' :
                       priceJobProgress.totalRows > 0 ? `Updating prices… (${priceJobProgress.processedCount} / ${priceJobProgress.totalRows} rows)` :
                       'Starting…'}
                    </span>
                  </div>
                  {priceJobProgress.status === 'running' && (
                    <button
                      onClick={handlePriceCancel}
                      disabled={priceCancelling}
                      className="shrink-0 flex items-center gap-1 px-2.5 py-1 rounded-lg border border-red-500/30 bg-red-500/10 hover:bg-red-500/20 text-[11px] text-red-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {priceCancelling ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />}
                      {priceCancelling ? 'Cancelling…' : 'Cancel'}
                    </button>
                  )}
                </div>
                {priceJobProgress.totalRows > 0 && priceJobProgress.processedCount >= 0 && (
                  <div className="space-y-1">
                    <div className="w-full bg-muted/50 rounded-full h-1.5 overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${
                          priceJobProgress.status === 'done' ? 'bg-emerald-400'
                          : priceJobProgress.status === 'failed' ? 'bg-red-400'
                          : 'bg-emerald-400'
                        }`}
                        style={{ width: `${Math.min(100, (priceJobProgress.processedCount / priceJobProgress.totalRows) * 100)}%` }}
                      />
                    </div>
                    <div className="text-[10px] text-slate-600 text-right">
                      {Math.round((priceJobProgress.processedCount / priceJobProgress.totalRows) * 100)}%
                    </div>
                  </div>
                )}
                {(priceJobProgress.successCount > 0 || priceJobProgress.skipCount > 0 || priceJobProgress.errorCount > 0) && (
                  <div className="flex items-center gap-4 text-[11px]">
                    {priceJobProgress.successCount > 0 && (
                      <span className="text-emerald-400">{priceJobProgress.successCount} prices updated</span>
                    )}
                    {priceJobProgress.skipCount > 0 && (
                      <span className="text-muted-foreground">{priceJobProgress.skipCount} not matched</span>
                    )}
                    {priceJobProgress.errorCount > 0 && (
                      <span className="text-red-400">{priceJobProgress.errorCount} errors</span>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
              {status.connectionType === 'api' ? (
                <>
                  <div className="text-muted-foreground">Connection</div>
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] font-medium uppercase tracking-wide bg-primary/10 text-primary border border-primary/20 px-1.5 py-0.5 rounded">API</span>
                    <span className="font-mono text-xs text-muted-foreground">{status.apiUrlMasked ?? '—'}</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-muted-foreground">Username</div>
                  <div className="font-mono text-foreground">{status.username}</div>
                </>
              )}

              <div className="text-muted-foreground">Target Store</div>
              <div className="text-foreground">
                {status.storeId
                  ? (stores.find(s => s.id === status.storeId)?.nickname ?? status.storeId)
                  : <span className="text-muted-foreground italic">Auto (most recent)</span>}
              </div>

              <div className="text-muted-foreground">Schedule</div>
              <div className="text-foreground">
                {status.scheduleEnabled
                  ? `${SCHEDULE_OPTIONS.find(o => o.value === status.scheduleInterval)?.label ?? status.scheduleInterval} at ${status.scheduleTime ?? '09:00'} (${TIMEZONE_OPTIONS.find(o => o.value === status.scheduleTimezone)?.label ?? status.scheduleTimezone ?? 'Melbourne'})`
                  : 'Disabled'}
              </div>

              <div className="text-muted-foreground">Column Mapping</div>
              <div className="text-foreground">
                {hasSavedMapping ? (
                  <span className="text-emerald-400 flex items-center gap-1 flex-wrap">
                    <CheckCircle2 className="w-3 h-3" />
                    SKU: <span className="font-mono text-xs ml-1">{status.columnMapping?.skuColumn ?? status.columnMapping?.variant_sku}</span>
                    <span className="mx-1 text-slate-600">·</span>
                    QTY: <span className="font-mono text-xs ml-1">{status.columnMapping?.qtyColumn ?? status.columnMapping?.variant_inventory_qty}</span>
                    {status.columnMapping?.descriptionColumn && (
                      <><span className="mx-1 text-slate-600">·</span>
                      DESC: <span className="font-mono text-xs ml-1">{status.columnMapping.descriptionColumn}</span></>
                    )}
                  </span>
                ) : (
                  <span className="text-amber-400 text-xs">Defaults: Code / Availability</span>
                )}
              </div>

              {status.scheduleEnabled && status.nextRunAt && (
                <>
                  <div className="text-muted-foreground">Next run</div>
                  <div className="text-foreground flex items-center gap-1.5">
                    <Clock className="w-3 h-3 text-muted-foreground" />
                    {formatDate(status.nextRunAt)}
                  </div>
                </>
              )}

              <div className="text-muted-foreground">Last run</div>
              <div className="text-foreground">{formatDate(status.lastRunAt)}</div>

              {status.lastRunStatus && (
                <>
                  <div className="text-muted-foreground">Last result</div>
                  <div className={`flex items-center gap-1.5 text-sm ${status.lastRunStatus === 'success' ? 'text-emerald-400' : 'text-red-400'}`}>
                    {status.lastRunStatus === 'success'
                      ? <CheckCircle2 className="w-3.5 h-3.5" />
                      : <AlertCircle className="w-3.5 h-3.5" />}
                    {status.lastRunStatus === 'success' ? 'Success' : status.lastRunError ?? 'Error'}
                  </div>
                </>
              )}
            </div>

            {status.lastRunStatus === 'success' && (
              <div className="flex items-start gap-2 text-[11px] text-muted-foreground pt-1">
                <RefreshCw className="w-3 h-3 shrink-0 mt-0.5" />
                Results are visible in the Import History page.
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-amber-500/10 border border-amber-500/20 text-sm text-amber-400">
            <AlertCircle className="w-4 h-4 shrink-0" />
            No Off Road Imports account connected. Enter your credentials below.
          </div>
        )}

        {/* ── Schedule & Store (shown only when already configured) ── */}
        {status?.configured && (
          <div className="glass-panel rounded-xl p-6 space-y-4">
            <h2 className="text-sm font-semibold text-foreground">Schedule &amp; Store</h2>

            <div className="space-y-4">
              <div className="space-y-1.5">
                {loadingStores ? (
                  <div className="flex items-center gap-2 text-muted-foreground text-xs py-2">
                    <Loader2 className="w-3 h-3 animate-spin" /> Loading stores…
                  </div>
                ) : stores.length === 0 ? (
                  <p className="text-xs text-amber-400/80 py-1">No stores connected. Add a store first.</p>
                ) : (
                  <>
                    <ShopifyTargetStoreSelector
                      stores={stores}
                      value={selectedStoreId}
                      onChange={setSelectedStoreId}
                      label="Target Store"
                    />
                    {stores.length > 1 && (
                      <button
                        type="button"
                        onClick={() => setSelectedStoreId('')}
                        className={cn(
                          'text-xs transition-colors',
                          !selectedStoreId ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
                        )}
                      >
                        {!selectedStoreId ? 'Currently using the most recently connected store' : 'Use automatically selected store'}
                      </button>
                    )}
                  </>
                )}
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Sync Schedule</label>
                <select value={scheduleOption} onChange={e => setScheduleOption(e.target.value)} className={inputCls}>
                  {SCHEDULE_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              {scheduleOption !== 'disabled' && (
                <>
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-muted-foreground">Sync Time</label>
                    <input
                      type="time"
                      value={scheduleTime}
                      onChange={e => setScheduleTime(e.target.value)}
                      className={inputCls}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-muted-foreground">Timezone</label>
                    <select value={scheduleTimezone} onChange={e => setScheduleTimezone(e.target.value)} className={inputCls}>
                      {TIMEZONE_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}
            </div>

            <div className="flex justify-end pt-1">
              <button
                onClick={handleSaveSettings}
                disabled={savingSettings}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary/20 hover:bg-primary/30 border border-primary/30 text-sm text-primary hover:text-foreground transition-all disabled:opacity-40"
              >
                {savingSettings ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save Settings
              </button>
            </div>
          </div>
        )}

        {/* ── Credentials — first-time connect OR update section ── */}
        {!status?.configured ? (
          /* First-time setup — full form */
          <div className="glass-panel rounded-xl p-6 space-y-5">
            <h2 className="text-sm font-semibold text-foreground">Connect Account</h2>

            <div className="space-y-4">
              {/* Connection type selector */}
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Connection Type</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { setFormConnectionType('scraper'); setTestResult(null); }}
                    className={`flex-1 py-2.5 rounded-xl border text-sm font-medium transition-all ${formConnectionType === 'scraper' ? 'bg-primary/15 border-primary/40 text-primary' : 'bg-muted/50 border-border text-muted-foreground hover:border-border'}`}
                  >
                    Username / Password
                  </button>
                  <button
                    type="button"
                    onClick={() => { setFormConnectionType('api'); setTestResult(null); }}
                    className={`flex-1 py-2.5 rounded-xl border text-sm font-medium transition-all ${formConnectionType === 'api' ? 'bg-primary/15 border-primary/40 text-primary' : 'bg-muted/50 border-border text-muted-foreground hover:border-border'}`}
                  >
                    API Key
                  </button>
                </div>
              </div>

              {/* Scraper fields */}
              {formConnectionType === 'scraper' && (<>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-muted-foreground">Username (email)</label>
                  <input
                    type="text"
                    autoComplete="off"
                    value={username}
                    onChange={e => setUsername(e.target.value)}
                    placeholder="you@example.com"
                    className={inputCls}
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-muted-foreground">Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="new-password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="Your Off Road Imports password"
                      className={`${inputCls} pr-10`}
                    />
                    <button type="button" onClick={() => setShowPassword(v => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
              </>)}

              {/* API fields */}
              {formConnectionType === 'api' && (
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-muted-foreground">API Link</label>
                  <input
                    type="text"
                    autoComplete="off"
                    value={apiUrl}
                    onChange={e => setApiUrl(e.target.value)}
                    placeholder="https://www.offroadimports.com.au/api/{key}/items/csv/list/…"
                    className={inputCls}
                  />
                  <p className="text-[11px] text-slate-600">Paste the full API URL. The date at the end is updated automatically on each sync.</p>
                </div>
              )}

              <div className="space-y-1.5">
                {loadingStores ? (
                  <div className="flex items-center gap-2 text-muted-foreground text-xs py-2">
                    <Loader2 className="w-3 h-3 animate-spin" /> Loading…
                  </div>
                ) : stores.length === 0 ? (
                  <p className="text-xs text-amber-400/80 py-1">No stores connected. Add a store first.</p>
                ) : (
                  <>
                    <ShopifyTargetStoreSelector
                      stores={stores}
                      value={selectedStoreId}
                      onChange={setSelectedStoreId}
                      label="Target Store"
                    />
                    {stores.length > 1 && (
                      <button
                        type="button"
                        onClick={() => setSelectedStoreId('')}
                        className={cn(
                          'text-xs transition-colors',
                          !selectedStoreId ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
                        )}
                      >
                        {!selectedStoreId ? 'Currently using the most recently connected store' : 'Use automatically selected store'}
                      </button>
                    )}
                  </>
                )}
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Sync Schedule</label>
                <select value={scheduleOption} onChange={e => setScheduleOption(e.target.value)} className={inputCls}>
                  {SCHEDULE_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              {scheduleOption !== 'disabled' && (
                <>
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-muted-foreground">Sync Time</label>
                    <input
                      type="time"
                      value={scheduleTime}
                      onChange={e => setScheduleTime(e.target.value)}
                      className={inputCls}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-muted-foreground">Timezone</label>
                    <select value={scheduleTimezone} onChange={e => setScheduleTimezone(e.target.value)} className={inputCls}>
                      {TIMEZONE_OPTIONS.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}
            </div>

            <div className="flex items-start gap-2 text-[11px] text-slate-600">
              <ShieldCheck className="w-3.5 h-3.5 text-muted-foreground shrink-0 mt-0.5" />
              Credentials are encrypted at rest using AES-256.
            </div>

            {testResult && (
              <div className={`flex items-start gap-2 px-3 py-2.5 rounded-xl border text-sm ${
                testResult.ok ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-red-500/10 border-red-500/20 text-red-400'
              }`}>
                {testResult.ok ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /> : <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />}
                <span>{testResult.ok ? (testResult.message ?? 'Connection successful') : (testResult.error ?? 'Connection failed')}</span>
              </div>
            )}

            <div className="flex items-center gap-3 pt-1">
              <button
                onClick={handleTest}
                disabled={testing || (formConnectionType === 'api' ? !apiUrl.trim() : (!username.trim() || !password.trim()))}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-muted/50 hover:bg-muted border border-border text-sm text-foreground hover:text-foreground transition-all disabled:opacity-40"
              >
                {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <TestTube className="w-4 h-4" />}
                Test Connection
              </button>
              <button
                onClick={handleSaveCredentials}
                disabled={saving || (formConnectionType === 'api' ? !apiUrl.trim() : (!username.trim() || !password.trim()))}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary/20 hover:bg-primary/30 border border-primary/30 text-sm text-primary hover:text-foreground transition-all disabled:opacity-40"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Connect Account
              </button>
            </div>
          </div>
        ) : (
          /* Already configured — collapsible update section */
          <div className="glass-panel rounded-xl overflow-hidden">
            <button
              onClick={() => { setShowCredentialUpdate(v => !v); setTestResult(null); setUsername(''); setPassword(''); setApiUrl(''); }}
              className="w-full flex items-center justify-between px-6 py-4 hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-center gap-2.5">
                <KeyRound className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-medium text-foreground">Change Connection</span>
                <span className="text-[11px] text-slate-600">Update credentials or switch connection type</span>
              </div>
              {showCredentialUpdate
                ? <ChevronDown className="w-4 h-4 text-muted-foreground" />
                : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
            </button>

            {showCredentialUpdate && (
              <div className="px-6 pb-6 space-y-4 border-t border-border/50 pt-4">

                {/* Connection type selector */}
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-muted-foreground">Connection Type</label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => { setFormConnectionType('scraper'); setTestResult(null); }}
                      className={`flex-1 py-2.5 rounded-xl border text-sm font-medium transition-all ${formConnectionType === 'scraper' ? 'bg-primary/15 border-primary/40 text-primary' : 'bg-muted/50 border-border text-muted-foreground hover:border-border'}`}
                    >
                      Username / Password
                    </button>
                    <button
                      type="button"
                      onClick={() => { setFormConnectionType('api'); setTestResult(null); }}
                      className={`flex-1 py-2.5 rounded-xl border text-sm font-medium transition-all ${formConnectionType === 'api' ? 'bg-primary/15 border-primary/40 text-primary' : 'bg-muted/50 border-border text-muted-foreground hover:border-border'}`}
                    >
                      API Key
                    </button>
                  </div>
                </div>

                {/* Scraper fields */}
                {formConnectionType === 'scraper' ? (<>
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-muted-foreground">Username (email)</label>
                    <input
                      type="text"
                      autoComplete="off"
                      value={username}
                      onChange={e => setUsername(e.target.value)}
                      placeholder={status.username ?? 'you@example.com'}
                      className={inputCls}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-muted-foreground">New Password</label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="new-password"
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        placeholder="New password"
                        className={`${inputCls} pr-10`}
                      />
                      <button type="button" onClick={() => setShowPassword(v => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                </>) : (
                  /* API field */
                  <div className="space-y-1.5">
                    <label className="text-xs font-medium text-muted-foreground">API Link</label>
                    <input
                      type="text"
                      autoComplete="off"
                      value={apiUrl}
                      onChange={e => setApiUrl(e.target.value)}
                      placeholder="https://www.offroadimports.com.au/api/{key}/items/csv/list/…"
                      className={inputCls}
                    />
                    <p className="text-[11px] text-slate-600">Paste the full API URL. The date is updated automatically on each sync.</p>
                  </div>
                )}

                <div className="flex items-start gap-2 text-[11px] text-slate-600">
                  <ShieldCheck className="w-3.5 h-3.5 text-muted-foreground shrink-0 mt-0.5" />
                  Credentials are encrypted at rest using AES-256.
                </div>

                {testResult && (
                  <div className={`flex items-start gap-2 px-3 py-2.5 rounded-xl border text-sm ${
                    testResult.ok ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400' : 'bg-red-500/10 border-red-500/20 text-red-400'
                  }`}>
                    {testResult.ok ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" /> : <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />}
                    <span>{testResult.ok ? (testResult.message ?? 'Connection successful') : (testResult.error ?? 'Connection failed')}</span>
                  </div>
                )}

                <div className="flex items-center gap-3 pt-1">
                  <button
                    onClick={handleTest}
                    disabled={testing || (formConnectionType === 'api' ? !apiUrl.trim() : (!username.trim() || !password.trim()))}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-muted/50 hover:bg-muted border border-border text-sm text-foreground hover:text-foreground transition-all disabled:opacity-40"
                  >
                    {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <TestTube className="w-4 h-4" />}
                    Test
                  </button>
                  <button
                    onClick={handleSaveCredentials}
                    disabled={saving || (formConnectionType === 'api' ? !apiUrl.trim() : (!username.trim() || !password.trim()))}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary/20 hover:bg-primary/30 border border-primary/30 text-sm text-primary hover:text-foreground transition-all disabled:opacity-40"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Update Connection
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Column Mapping (shown when CSV headers are available) ── */}
        {showColumnMapper && (
          <div className="glass-panel rounded-xl p-6 space-y-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <Columns className="w-4 h-4 text-primary" />
                  <h2 className="text-sm font-semibold text-foreground">Column Mapping</h2>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Map the two ORI stock CSV columns to their Shopify purpose.
                  {hasSavedMapping ? ' Your saved mapping is loaded below.' : ''}
                </p>
              </div>
              {hasSavedMapping && (
                <span className="shrink-0 flex items-center gap-1 text-[11px] text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-1 rounded-md">
                  <CheckCircle2 className="w-3 h-3" /> Saved
                </span>
              )}
            </div>

            <div className="flex items-center gap-3 px-3 pb-1 text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
              <div className="flex-1">Shopify Purpose</div>
              <div className="w-4" />
              <div className="flex-1">CSV Column</div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-3 px-3 py-3 rounded-xl bg-muted/50 border border-primary/20">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <Hash className="w-3.5 h-3.5 text-primary shrink-0" />
                    <p className="text-sm text-foreground font-medium">SKU Identifier</p>
                    <span className="text-red-400 text-xs">*</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-0.5 pl-5">Matches each row to a Shopify variant by SKU</p>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <select value={skuColumn} onChange={e => setSkuColumn(e.target.value)} className={selectCls}>
                    <option value="">— Select column —</option>
                    {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                  </select>
                </div>
              </div>

              <div className="flex items-center gap-3 px-3 py-3 rounded-xl bg-muted/50 border border-border/50 hover:border-border transition-all">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <Package className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                    <p className="text-sm text-foreground font-medium">Inventory Quantity</p>
                    <span className="text-red-400 text-xs">*</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-0.5 pl-5">Sets the stock level on Shopify for the matched variant</p>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <select value={qtyColumn} onChange={e => setQtyColumn(e.target.value)} className={selectCls}>
                    <option value="">— Select column —</option>
                    {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                  </select>
                </div>
              </div>

              <div className="flex items-center gap-3 px-3 py-3 rounded-xl bg-muted/50 border border-border/50 hover:border-border transition-all">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <svg className="w-3.5 h-3.5 text-muted-foreground shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
                    <p className="text-sm text-foreground font-medium">Product Description</p>
                    <span className="text-slate-500 text-xs">(optional)</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-0.5 pl-5">Updates the Shopify product description (body HTML) during each sync</p>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <select value={descriptionColumn} onChange={e => setDescriptionColumn(e.target.value)} className={selectCls}>
                    <option value="">— Not mapped —</option>
                    {csvHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {csvSampleRows.length > 0 && skuColumn && qtyColumn && (
              <div className="space-y-2 pt-1">
                <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                  <Eye className="w-3.5 h-3.5 text-primary" />
                  Data preview — first {csvSampleRows.length} rows
                </p>
                <div className="overflow-x-auto rounded-xl border border-border">
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="border-b border-border bg-muted/50">
                        <th className="px-3 py-2 text-left font-medium text-muted-foreground">SKU <span className="text-slate-600 font-normal ml-1">({skuColumn})</span></th>
                        <th className="px-3 py-2 text-left font-medium text-muted-foreground">Qty <span className="text-slate-600 font-normal ml-1">({qtyColumn})</span></th>
                        {descriptionColumn && (
                          <th className="px-3 py-2 text-left font-medium text-muted-foreground">Description <span className="text-slate-600 font-normal ml-1">({descriptionColumn})</span></th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {csvSampleRows.map((row, i) => (
                        <tr key={i} className={`border-b border-border/50 last:border-0 ${i % 2 === 0 ? '' : 'bg-muted/20'}`}>
                          <td className="px-3 py-2 font-mono text-foreground">{row[skuColumn] ?? <span className="text-slate-600 italic">—</span>}</td>
                          <td className="px-3 py-2 font-mono text-foreground">{row[qtyColumn] ?? <span className="text-slate-600 italic">—</span>}</td>
                          {descriptionColumn && (
                            <td className="px-3 py-2 text-foreground max-w-xs truncate" title={row[descriptionColumn]}>{row[descriptionColumn] || <span className="text-slate-600 italic">—</span>}</td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between pt-1">
              <p className="text-[11px] text-slate-600">{csvHeaders.length} column{csvHeaders.length !== 1 ? 's' : ''} detected</p>
              <button onClick={handleSaveMapping} disabled={savingMapping || !mappingHasRequired}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary/20 hover:bg-primary/30 border border-primary/30 text-sm text-primary hover:text-foreground transition-all disabled:opacity-40">
                {savingMapping ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save Mapping
              </button>
            </div>
          </div>
        )}

        {/* Prompt to test first when no headers loaded yet */}
        {status?.configured && !showColumnMapper && (
          <div className="glass-panel rounded-xl p-5 space-y-3">
            <div className="flex items-center gap-2">
              <Columns className="w-4 h-4 text-primary" />
              <h2 className="text-sm font-semibold text-foreground">Column Mapping</h2>
            </div>
            {hasSavedMapping ? (
              <>
                <p className="text-xs text-muted-foreground">
                  Your column mapping is saved. To update it, expand Change Credentials above and click Test to reload the CSV.
                </p>
                <div className="flex flex-wrap gap-2 pt-1">
                  <span className="text-[11px] bg-muted/50 border border-border rounded-lg px-2 py-1 text-muted-foreground">
                    <span className="text-muted-foreground">SKU column</span>
                    <ArrowRight className="w-2.5 h-2.5 inline mx-1 text-slate-600" />
                    <span className="text-foreground font-mono">{status.columnMapping?.skuColumn ?? status.columnMapping?.variant_sku ?? '—'}</span>
                  </span>
                  <span className="text-[11px] bg-muted/50 border border-border rounded-lg px-2 py-1 text-muted-foreground">
                    <span className="text-muted-foreground">QTY column</span>
                    <ArrowRight className="w-2.5 h-2.5 inline mx-1 text-slate-600" />
                    <span className="text-foreground font-mono">{status.columnMapping?.qtyColumn ?? status.columnMapping?.variant_inventory_qty ?? '—'}</span>
                  </span>
                  {status.columnMapping?.descriptionColumn && (
                    <span className="text-[11px] bg-muted/50 border border-border rounded-lg px-2 py-1 text-muted-foreground">
                      <span className="text-muted-foreground">Description column</span>
                      <ArrowRight className="w-2.5 h-2.5 inline mx-1 text-slate-600" />
                      <span className="text-foreground font-mono">{status.columnMapping.descriptionColumn}</span>
                    </span>
                  )}
                </div>
              </>
            ) : (
              <p className="text-xs text-muted-foreground">
                Expand <span className="text-foreground">Change Credentials</span> above and click Test to load the CSV columns.
              </p>
            )}
          </div>
        )}

        {/* ── Price Sync Column Setup ── */}
        {status?.configured && isPriceSyncAvailable && (
          <div className="glass-panel rounded-xl p-5 space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-emerald-400" />
                  <h2 className="text-sm font-semibold text-foreground">Price Sync Columns</h2>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Maps columns from the ORI XLSX price list. Price Sync updates Shopify variant prices only — no inventory is changed.
                </p>
              </div>
              {hasSavedPriceMapping && (
                <span className="shrink-0 flex items-center gap-1 text-[11px] text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2 py-1 rounded-md">
                  <CheckCircle2 className="w-3 h-3" /> Saved
                </span>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-3 px-3 py-3 rounded-xl bg-muted/50 border border-border/50">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <Hash className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                    <p className="text-sm text-foreground font-medium">SKU Column</p>
                    <span className="text-red-400 text-xs">*</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-0.5 pl-5">Column in the XLSX that holds the product SKU/code</p>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <input
                    type="text"
                    value={priceSkuColumn}
                    onChange={e => setPriceSkuColumn(e.target.value)}
                    placeholder="e.g. Code"
                    className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50"
                  />
                </div>
              </div>

              <div className="flex items-center gap-3 px-3 py-3 rounded-xl bg-muted/50 border border-border/50">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <TrendingUp className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                    <p className="text-sm text-foreground font-medium">Price Column</p>
                    <span className="text-red-400 text-xs">*</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-0.5 pl-5">Column in the XLSX that holds the retail price (RRP)</p>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <input
                    type="text"
                    value={priceColumn}
                    onChange={e => setPriceColumn(e.target.value)}
                    placeholder="e.g. RRP"
                    className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary/50"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between pt-1">
              <p className="text-[11px] text-slate-500">
                Not sure of the column names? Open the catalog tab to browse the XLSX headers.
              </p>
              <button
                onClick={handleSavePriceMapping}
                disabled={savingPriceMapping || !priceSkuColumn || !priceColumn}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/20 text-sm text-emerald-400 hover:text-emerald-300 transition-all disabled:opacity-40"
              >
                {savingPriceMapping ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save
              </button>
            </div>
          </div>
        )}

        {/* ── Run Logs ── */}
        {status?.configured && (
          <div className="glass-panel rounded-xl overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border/50">
              <div className="flex items-center gap-2.5">
                <History className="w-4 h-4 text-primary" />
                <h2 className="text-sm font-semibold text-foreground">Run History</h2>
                {logs.length > 0 && (
                  <span className="text-[11px] text-slate-600">{logs.length} recent run{logs.length !== 1 ? 's' : ''}</span>
                )}
              </div>
              <button onClick={loadLogs} disabled={loadingLogs}
                className="flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40">
                <RefreshCw className={`w-3 h-3 ${loadingLogs ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>

            {loadingLogs && logs.length === 0 ? (
              <div className="px-6 py-8 flex items-center justify-center gap-2 text-muted-foreground text-sm">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading logs…
              </div>
            ) : logs.length === 0 ? (
              <div className="px-6 py-8 text-center text-slate-600 text-sm">
                No sync runs yet. Click <span className="text-muted-foreground">Run Now</span> to start your first sync.
              </div>
            ) : (
              <div className="divide-y divide-white/5">
                {logs.map(log => {
                  const isExpanded = expandedLogId === log.id;
                  const filteredDetails = (log.details ?? []).filter(d =>
                    logFilter === 'all' || d.status === logFilter
                  );
                  const changedCount = (log.details ?? []).filter(d => d.status === 'updated' && d.oldQty !== d.newQty).length;
                  const unchangedCount = (log.details ?? []).filter(d => d.status === 'updated' && d.oldQty === d.newQty).length;

                  return (
                    <div key={log.id}>
                      {/* Run summary row — clickable to expand */}
                      <button
                        onClick={() => setExpandedLogId(isExpanded ? null : log.id)}
                        className="w-full flex items-center gap-4 px-6 py-4 hover:bg-muted/50 transition-colors text-left"
                      >
                        <div className="shrink-0">
                          {log.status === 'done'
                            ? <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                            : log.status === 'failed'
                            ? <XCircle className="w-4 h-4 text-red-400" />
                            : log.status === 'cancelled'
                            ? <XCircle className="w-4 h-4 text-amber-400" />
                            : <Loader2 className="w-4 h-4 text-muted-foreground animate-spin" />}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-3 flex-wrap">
                            <span className="text-xs text-muted-foreground">{fmtDateTime(log.createdAt)}</span>
                            {log.storeName && (
                              <span className="text-[11px] text-slate-600">{log.storeName}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 mt-1 flex-wrap">
                            {log.status === 'cancelled' && (
                              <span className="text-[11px] font-medium text-amber-400 bg-amber-400/10 border border-amber-400/20 rounded px-1.5 py-0.5">
                                Cancelled
                              </span>
                            )}
                            <span className="text-[11px] text-emerald-400">{log.successCount} updated</span>
                            {log.skipCount > 0 && <span className="text-[11px] text-muted-foreground">{log.skipCount} not in Shopify</span>}
                            {log.errorCount > 0 && <span className="text-[11px] text-red-400">{log.errorCount} errors</span>}
                            {changedCount > 0 && <span className="text-[11px] text-primary">{changedCount} qty changed</span>}
                            {unchangedCount > 0 && <span className="text-[11px] text-slate-600">{unchangedCount} unchanged</span>}
                          </div>
                        </div>

                        <div className="shrink-0 flex items-center gap-2">
                          {(log.status === 'cancelled' || log.status === 'failed') && (
                            <button
                              onClick={e => { e.stopPropagation(); handleRunNow(); }}
                              disabled={running}
                              title="Retry sync"
                              className="flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] text-primary hover:text-primary/80 hover:bg-primary/10 border border-primary/20 transition-colors disabled:opacity-40"
                            >
                              {running
                                ? <Loader2 className="w-3 h-3 animate-spin" />
                                : <RefreshCw className="w-3 h-3" />}
                              <span>Retry</span>
                            </button>
                          )}
                          {log.details && log.details.length > 0 && (
                            <button
                              onClick={e => { e.stopPropagation(); exportCsv(log.id, log.createdAt); }}
                              disabled={exportingJobId === log.id}
                              title="Export to CSV"
                              className="flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-40"
                            >
                              {exportingJobId === log.id
                                ? <Loader2 className="w-3 h-3 animate-spin" />
                                : <Download className="w-3 h-3" />}
                              <span>Export</span>
                            </button>
                          )}
                          <span className="text-slate-600">
                            {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </span>
                        </div>
                      </button>

                      {/* Expanded per-SKU detail table */}
                      {isExpanded && log.details && log.details.length > 0 && (
                        <div className="px-4 pb-4 space-y-3 border-t border-border/50 bg-muted/50">
                          {/* Filter tabs */}
                          <div className="flex items-center gap-1 pt-3 flex-wrap">
                            {(['all', 'updated', 'not_found', 'error'] as const).map(f => {
                              const counts = {
                                all: log.details!.length,
                                updated: log.details!.filter(d => d.status === 'updated').length,
                                not_found: log.details!.filter(d => d.status === 'not_found').length,
                                error: log.details!.filter(d => d.status === 'error').length,
                              };
                              if (f !== 'all' && counts[f] === 0) return null;
                              const labels = { all: 'All', updated: 'Updated', not_found: 'Not found', error: 'Errors' };
                              return (
                                <button
                                  key={f}
                                  onClick={() => setLogFilter(f)}
                                  className={`px-2.5 py-1 rounded-lg text-[11px] transition-colors ${
                                    logFilter === f
                                      ? 'bg-primary/20 text-primary border border-primary/30'
                                      : 'text-muted-foreground hover:text-foreground border border-transparent'
                                  }`}
                                >
                                  {labels[f]} <span className="opacity-60">({counts[f]})</span>
                                </button>
                              );
                            })}
                          </div>

                          <div className="overflow-x-auto rounded-xl border border-border max-h-96 overflow-y-auto">
                            <table className="w-full text-xs border-collapse">
                              <thead className="sticky top-0 z-10">
                                <tr className="border-b border-border bg-card">
                                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">SKU</th>
                                  <th className="px-3 py-2 text-right font-medium text-muted-foreground">Old Qty</th>
                                  <th className="px-3 py-2 text-center font-medium text-muted-foreground w-8"></th>
                                  <th className="px-3 py-2 text-right font-medium text-muted-foreground">New Qty</th>
                                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">Change</th>
                                </tr>
                              </thead>
                              <tbody>
                                {filteredDetails.slice(0, 500).map((d, i) => {
                                  const diff = d.oldQty != null ? d.newQty - d.oldQty : null;
                                  const increased = diff != null && diff > 0;
                                  const decreased = diff != null && diff < 0;
                                  return (
                                    <tr key={i} className={`border-b border-border/50 last:border-0 ${i % 2 === 0 ? '' : 'bg-muted/20'}`}>
                                      <td className="px-3 py-1.5 font-mono text-foreground">
                                        {d.status === 'not_found'
                                          ? <span className="text-slate-600">{d.sku}</span>
                                          : d.status === 'error'
                                          ? <span className="text-red-400" title={d.error}>{d.sku}</span>
                                          : d.sku}
                                      </td>
                                      <td className="px-3 py-1.5 text-right font-mono text-muted-foreground">
                                        {d.status === 'not_found' ? <span className="text-slate-600">—</span> : (d.oldQty ?? '—')}
                                      </td>
                                      <td className="px-3 py-1.5 text-center text-slate-700">
                                        {d.status !== 'not_found' && <ArrowRight className="w-3 h-3 inline" />}
                                      </td>
                                      <td className="px-3 py-1.5 text-right font-mono">
                                        {d.status === 'not_found'
                                          ? <span className="text-slate-600 italic text-[10px]">not in Shopify</span>
                                          : d.status === 'error'
                                          ? <span className="text-red-400 italic text-[10px]">error</span>
                                          : <span className={increased ? 'text-emerald-400' : decreased ? 'text-red-400' : 'text-muted-foreground'}>{d.newQty}</span>}
                                      </td>
                                      <td className="px-3 py-1.5">
                                        {d.status === 'not_found' ? null
                                          : d.status === 'error' ? <span className="text-red-400 italic text-[10px]" title={d.error}>failed</span>
                                          : diff == null ? null
                                          : diff === 0 ? <span className="text-slate-700 flex items-center gap-0.5"><Minus className="w-2.5 h-2.5" />same</span>
                                          : increased ? <span className="text-emerald-400 flex items-center gap-0.5"><TrendingUp className="w-2.5 h-2.5" />+{diff}</span>
                                          : <span className="text-red-400 flex items-center gap-0.5"><TrendingDown className="w-2.5 h-2.5" />{diff}</span>}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                            {filteredDetails.length > 500 && (
                              <p className="text-center text-[11px] text-slate-600 py-2">
                                Showing first 500 of {filteredDetails.length} rows
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
        </>)}

        {/* ── Product Catalogue ── */}
        {section === 'import' && status?.configured && status.connectionType === 'scraper' && (
          <div className="glass-panel rounded-xl overflow-hidden border border-border/50">
            {/* Collapsible header */}
            <button
              onClick={() => setCatalogOpen(v => !v)}
              className="w-full flex items-center gap-3 px-6 py-4 hover:bg-muted/30 transition-colors"
            >
              <FileSpreadsheet className="w-4 h-4 text-primary/70 flex-shrink-0" />
              <div className="flex-1 text-left">
                <span className="text-sm font-medium text-foreground">Product Catalogue</span>
                <span className="block text-[11px] text-slate-600 mt-0.5">Download the ORI XLSX price list and import products to Shopify</span>
              </div>
              {catalogRows.length > 0 && (
                <span className="text-[11px] text-muted-foreground bg-muted/50 px-2 py-0.5 rounded-full mr-1">
                  {catalogRows.length.toLocaleString()} products
                </span>
              )}
              {catalogOpen ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
            </button>

            {catalogOpen && (
              <div className="border-t border-border/50 p-6 space-y-5">

                {/* Load button */}
                <div className="flex items-center gap-3 flex-wrap">
                  <button
                    onClick={handleFetchCatalog}
                    disabled={catalogLoading}
                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-primary/10 hover:bg-primary/15 border border-primary/20 text-sm text-primary transition-all disabled:opacity-50"
                  >
                    {catalogLoading
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <Download className="w-4 h-4" />}
                    {catalogLoading ? 'Downloading catalogue…' : catalogRows.length > 0 ? 'Refresh Catalogue' : 'Load ORI Catalogue'}
                  </button>
                  {catalogRows.length > 0 && !catalogLoading && (
                    <span className="text-xs text-muted-foreground">{catalogRows.length.toLocaleString()} products loaded</span>
                  )}
                </div>

                {/* Error */}
                {catalogError && (
                  <div className="flex items-start gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/20">
                    <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-400">{catalogError}</p>
                  </div>
                )}

                {/* Content — only shown once rows are loaded */}
                {catalogRows.length > 0 && (
                  <>
                    {/* Column mapping */}
                    <div className="space-y-3">
                      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                        <Columns className="w-3 h-3" /> Column Mapping
                      </h3>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {([
                          { field: 'title',       label: 'Product Title' },
                          { field: 'description', label: 'Description'   },
                          { field: 'sku',         label: 'SKU / Code'    },
                          { field: 'price',       label: 'Price (RRP)'   },
                          { field: 'cost',        label: 'Cost Price'    },
                          { field: 'brand',       label: 'Brand / Vendor'},
                          { field: 'category',    label: 'Category / Type'},
                          { field: 'barcode',     label: 'Barcode'       },
                        ] as const).map(({ field, label }) => (
                          <div key={field} className="space-y-1">
                            <label className="text-[11px] text-muted-foreground">{label}</label>
                            <select
                              value={catalogColMap[field]}
                              onChange={e => setCatalogColMap(m => ({ ...m, [field]: e.target.value }))}
                              className={selectCls}
                            >
                              <option value="">(not mapped)</option>
                              {catalogHeaders.map(h => (
                                <option key={h} value={h}>{h}</option>
                              ))}
                            </select>
                          </div>
                        ))}
                        {catalogColMap.imageUrls.map((val, idx) => (
                          <div key={`imageUrls-${idx}`} className="space-y-1">
                            <label className="text-[11px] text-muted-foreground">Image URL {idx + 1}{idx === 0 ? '' : ' (optional)'}</label>
                            <select
                              value={val}
                              onChange={e => setCatalogColMap(m => {
                                const next = [...m.imageUrls];
                                next[idx] = e.target.value;
                                return { ...m, imageUrls: next };
                              })}
                              className={selectCls}
                            >
                              <option value="">(not mapped)</option>
                              {catalogHeaders.map(h => (
                                <option key={h} value={h}>{h}</option>
                              ))}
                            </select>
                          </div>
                        ))}
                      </div>
                      <p className="text-[11px] text-slate-600">
                        Map up to 5 image URL columns — all mapped images will be imported to Shopify for each product.
                      </p>
                    </div>

                    {/* Search + selection controls */}
                    <div className="flex items-center gap-3 flex-wrap">
                      {catalogColMap.category && catalogTypeOptions.length > 0 && (
                        <select
                          value={catalogTypeFilter}
                          onChange={e => { setCatalogTypeFilter(e.target.value); setCatalogPage(0); }}
                          className={`${inputCls} py-2 w-auto min-w-40`}
                        >
                          <option value="">All product types</option>
                          {catalogTypeOptions.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      )}
                      <div className="relative flex-1 min-w-48">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-600" />
                        <input
                          type="text"
                          value={catalogSearch}
                          onChange={e => { setCatalogSearch(e.target.value); setCatalogPage(0); }}
                          placeholder="Filter products…"
                          className={`${inputCls} pl-8 py-2`}
                        />
                      </div>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {catalogSelected.size.toLocaleString()} of {catalogRows.length.toLocaleString()} selected
                      </span>
                      <button
                        onClick={() => setCatalogSelected(prev => {
                          const next = new Set(prev);
                          filteredCatalog.forEach(({ absIdx }) => next.add(absIdx));
                          return next;
                        })}
                        className="text-xs text-primary hover:text-primary/70 transition-colors whitespace-nowrap"
                      >
                        Select all
                      </button>
                      <button
                        onClick={() => setCatalogSelected(prev => {
                          const next = new Set(prev);
                          filteredCatalog.forEach(({ absIdx }) => next.delete(absIdx));
                          return next;
                        })}
                        className="text-xs text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap"
                      >
                        Deselect all
                      </button>
                      <div className="flex items-center gap-0.5 bg-muted/50 border border-border rounded-lg p-0.5 ml-auto">
                        <button
                          type="button"
                          onClick={() => setCatalogViewMode('tiles')}
                          title="Tile view"
                          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] transition-colors ${
                            catalogViewMode === 'tiles' ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          <LayoutGrid className="w-3.5 h-3.5" /> Tiles
                        </button>
                        <button
                          type="button"
                          onClick={() => setCatalogViewMode('table')}
                          title="Table view"
                          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] transition-colors ${
                            catalogViewMode === 'table' ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          <ListIcon className="w-3.5 h-3.5" /> Table
                        </button>
                      </div>
                    </div>

                    {/* Product view — tiles or table */}
                    {catalogViewMode === 'tiles' ? (
                      <div className="rounded-xl border border-border max-h-[32rem] overflow-y-auto p-3">
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                          {paginatedCatalog.map(({ row, absIdx }) => {
                            const checked = catalogSelected.has(absIdx);
                            const imageUrl = getRowImageUrl(row);
                            const title = catalogColMap.title ? row[catalogColMap.title] : '';
                            const sku = catalogColMap.sku ? row[catalogColMap.sku] : '';
                            const price = catalogColMap.price ? row[catalogColMap.price] : '';
                            return (
                              <div
                                key={absIdx}
                                onClick={() => setCatalogSelected(prev => {
                                  const next = new Set(prev);
                                  if (next.has(absIdx)) next.delete(absIdx); else next.add(absIdx);
                                  return next;
                                })}
                                className={`relative rounded-xl border overflow-hidden cursor-pointer transition-all ${
                                  checked ? 'border-primary/50 bg-primary/5 ring-1 ring-primary/30' : 'border-border bg-muted/20 hover:border-border'
                                }`}
                              >
                                <div className="absolute top-2 left-2 z-10" onClick={e => e.stopPropagation()}>
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => setCatalogSelected(prev => {
                                      const next = new Set(prev);
                                      if (next.has(absIdx)) next.delete(absIdx); else next.add(absIdx);
                                      return next;
                                    })}
                                    className="accent-primary w-4 h-4"
                                  />
                                </div>
                                <div className="aspect-square bg-muted/50 flex items-center justify-center overflow-hidden">
                                  {imageUrl ? (
                                    <img
                                      src={imageUrl}
                                      alt={title || sku || 'Product'}
                                      loading="lazy"
                                      className="w-full h-full object-contain"
                                      onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                    />
                                  ) : (
                                    <ImageIcon className="w-8 h-8 text-slate-700" />
                                  )}
                                </div>
                                <div className="p-2.5 space-y-1">
                                  <p className="text-xs text-foreground leading-snug line-clamp-2 min-h-[2.2em]" title={title}>
                                    {title || <span className="text-slate-600 italic">Untitled</span>}
                                  </p>
                                  <div className="flex items-center justify-between gap-2">
                                    {sku && <span className="text-[10px] font-mono text-muted-foreground truncate">{sku}</span>}
                                    {price && <span className="text-[11px] font-medium text-primary whitespace-nowrap">{price}</span>}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                    <div className="overflow-auto rounded-xl border border-border max-h-96">
                      <table className="w-full text-xs border-collapse">
                        <thead className="sticky top-0 z-10 bg-card">
                          <tr className="border-b border-border">
                            <th className="px-3 py-2 w-8">
                              <input
                                type="checkbox"
                                checked={
                                  filteredCatalog.length > 0 &&
                                  filteredCatalog.every(({ absIdx }) => catalogSelected.has(absIdx))
                                }
                                onChange={e => {
                                  if (e.target.checked) {
                                    setCatalogSelected(prev => {
                                      const next = new Set(prev);
                                      filteredCatalog.forEach(({ absIdx }) => next.add(absIdx));
                                      return next;
                                    });
                                  } else {
                                    setCatalogSelected(prev => {
                                      const next = new Set(prev);
                                      filteredCatalog.forEach(({ absIdx }) => next.delete(absIdx));
                                      return next;
                                    });
                                  }
                                }}
                                className="accent-primary"
                              />
                            </th>
                            {catalogHasImageMapping && <th className="px-3 py-2 w-12"></th>}
                            {catalogColMap.sku      && <th className="px-3 py-2 text-left font-medium text-muted-foreground">SKU</th>}
                            {catalogColMap.title    && <th className="px-3 py-2 text-left font-medium text-muted-foreground">Title</th>}
                            {catalogColMap.brand    && <th className="px-3 py-2 text-left font-medium text-muted-foreground">Brand</th>}
                            {catalogColMap.category && <th className="px-3 py-2 text-left font-medium text-muted-foreground">Category</th>}
                            {catalogColMap.price    && <th className="px-3 py-2 text-right font-medium text-muted-foreground">Price</th>}
                            {catalogColMap.cost     && <th className="px-3 py-2 text-right font-medium text-muted-foreground">Cost</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedCatalog.map(({ row, absIdx }, i) => {
                            const checked = catalogSelected.has(absIdx);
                            return (
                              <tr
                                key={absIdx}
                                onClick={() => setCatalogSelected(prev => {
                                  const next = new Set(prev);
                                  if (next.has(absIdx)) next.delete(absIdx); else next.add(absIdx);
                                  return next;
                                })}
                                className={`border-b border-border/50 last:border-0 cursor-pointer transition-colors ${
                                  checked ? 'bg-primary/5' : i % 2 === 0 ? '' : 'bg-muted/20'
                                } hover:bg-muted/50`}
                              >
                                <td className="px-3 py-1.5" onClick={e => e.stopPropagation()}>
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => setCatalogSelected(prev => {
                                      const next = new Set(prev);
                                      if (next.has(absIdx)) next.delete(absIdx); else next.add(absIdx);
                                      return next;
                                    })}
                                    className="accent-primary"
                                  />
                                </td>
                                {catalogHasImageMapping && (
                                  <td className="px-3 py-1.5">
                                    {getRowImageUrl(row) ? (
                                      <img
                                        src={getRowImageUrl(row)}
                                        alt=""
                                        loading="lazy"
                                        className="w-8 h-8 object-contain rounded bg-muted/50"
                                        onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                      />
                                    ) : (
                                      <div className="w-8 h-8 rounded bg-muted/50 flex items-center justify-center">
                                        <ImageIcon className="w-3.5 h-3.5 text-slate-700" />
                                      </div>
                                    )}
                                  </td>
                                )}
                                {catalogColMap.sku      && <td className="px-3 py-1.5 font-mono text-muted-foreground whitespace-nowrap">{row[catalogColMap.sku]}</td>}
                                {catalogColMap.title    && <td className="px-3 py-1.5 text-foreground max-w-xs truncate">{row[catalogColMap.title]}</td>}
                                {catalogColMap.brand    && <td className="px-3 py-1.5 text-muted-foreground whitespace-nowrap">{row[catalogColMap.brand]}</td>}
                                {catalogColMap.category && <td className="px-3 py-1.5 text-muted-foreground whitespace-nowrap">{row[catalogColMap.category]}</td>}
                                {catalogColMap.price    && <td className="px-3 py-1.5 text-right text-foreground whitespace-nowrap">{row[catalogColMap.price]}</td>}
                                {catalogColMap.cost     && <td className="px-3 py-1.5 text-right text-muted-foreground whitespace-nowrap">{row[catalogColMap.cost]}</td>}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    )}

                    {filteredCatalog.length > 0 && (
                      <div className="flex items-center justify-between gap-3 flex-wrap text-xs text-muted-foreground">
                        <span>
                          Showing {(catalogPage * CATALOG_PAGE_SIZE + 1).toLocaleString()}
                          {'\u2013'}
                          {Math.min((catalogPage + 1) * CATALOG_PAGE_SIZE, filteredCatalog.length).toLocaleString()} of{' '}
                          {filteredCatalog.length.toLocaleString()}
                        </span>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setCatalogPage(p => Math.max(0, p - 1))}
                            disabled={catalogPage === 0}
                            className="px-2.5 py-1 rounded-md border border-border hover:bg-muted/50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                          >
                            Previous
                          </button>
                          <span className="whitespace-nowrap">
                            Page {catalogPage + 1} of {catalogTotalPages}
                          </span>
                          <button
                            type="button"
                            onClick={() => setCatalogPage(p => Math.min(catalogTotalPages - 1, p + 1))}
                            disabled={catalogPage >= catalogTotalPages - 1}
                            className="px-2.5 py-1 rounded-md border border-border hover:bg-muted/50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Import controls */}
                    <div className="flex items-end gap-3 flex-wrap pt-1 border-t border-border/50">
                      <ShopifyTargetStoreSelector
                        stores={stores.filter(s => s.status === 'active')}
                        value={catalogImportStoreId}
                        onChange={setCatalogImportStoreId}
                        label="Target Store"
                        className="flex-1 min-w-40"
                      />
                      <div className="min-w-40 space-y-1">
                        <label className="text-xs font-medium text-muted-foreground">If product exists</label>
                        <select
                          value={catalogDupHandling}
                          onChange={e => setCatalogDupHandling(e.target.value as 'skip' | 'overwrite' | 'create')}
                          className={selectCls}
                        >
                          <option value="skip">Skip (keep existing)</option>
                          <option value="overwrite">Overwrite</option>
                          <option value="create">Always create new</option>
                        </select>
                      </div>
                      <button
                        onClick={handleCatalogImport}
                        disabled={catalogImporting || catalogSelected.size === 0 || !catalogImportStoreId}
                        className="flex items-center gap-2 px-5 py-2 rounded-xl bg-emerald-500/10 hover:bg-emerald-500/15 border border-emerald-500/20 text-sm text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 transition-all disabled:opacity-40"
                      >
                        {catalogImporting
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : <ShoppingBag className="w-4 h-4" />}
                        {catalogImporting
                          ? `Importing… ${catalogImportProgress ? `${catalogImportProgress.current} / ${catalogImportProgress.total}` : ''}`
                          : `Import ${catalogSelected.size.toLocaleString()} to Shopify`}
                      </button>
                    </div>

                    {/* Progress bar */}
                    {catalogImporting && catalogImportProgress && catalogImportProgress.total > 0 && (
                      <div className="space-y-1">
                        <div className="h-1.5 w-full bg-muted/50 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-emerald-500/70 rounded-full transition-all duration-300"
                            style={{ width: `${(catalogImportProgress.current / catalogImportProgress.total) * 100}%` }}
                          />
                        </div>
                        <p className="text-[11px] text-slate-600 text-right">
                          {catalogImportProgress.current.toLocaleString()} / {catalogImportProgress.total.toLocaleString()} products
                        </p>
                      </div>
                    )}

                    {/* Result summary */}
                    {catalogImportResult && (
                      <div className={`flex items-start gap-3 p-4 rounded-xl border ${
                        catalogImportResult.errorCount === 0
                          ? 'bg-emerald-500/10 border-emerald-500/20'
                          : 'bg-amber-500/10 border-amber-500/20'
                      }`}>
                        <CheckCircle2 className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                          catalogImportResult.errorCount === 0 ? 'text-emerald-400' : 'text-amber-400'
                        }`} />
                        <div className="space-y-1 min-w-0">
                          <p className="text-sm font-medium text-foreground">Import complete</p>
                          <p className="text-xs text-muted-foreground">
                            {catalogImportResult.successCount} created &middot; {catalogImportResult.skipCount} skipped &middot; {catalogImportResult.errorCount} failed
                          </p>
                          {catalogImportResult.errors.length > 0 && (
                            <div className="mt-2 space-y-1 max-h-28 overflow-y-auto">
                              {catalogImportResult.errors.map((e, i) => (
                                <p key={i} className="text-[11px] text-red-400 font-mono truncate" title={`${e.title}: ${e.error}`}>
                                  {e.title}: {e.error}
                                </p>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  {/* ── Update Existing Listings ── */}
                  <div className="pt-2 border-t border-border/50 space-y-4">
                    <div>
                      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        Update Existing Listings
                      </h3>
                      <p className="text-[11px] text-muted-foreground mt-1">
                        Push selected field values from the catalogue to products already in Shopify, matched by SKU. No new products will be created.
                        {catalogSelected.size === 0 && catalogRows.length > 0 && (
                          <span className="text-amber-400/80"> All {catalogRows.length.toLocaleString()} rows will be used (select specific rows above to limit).</span>
                        )}
                      </p>
                    </div>

                    {/* Field checkboxes */}
                    <div className="space-y-1.5">
                      <p className="text-[11px] font-medium text-muted-foreground">Fields to update:</p>
                      <div className="flex flex-wrap gap-2">
                        {([
                          { key: 'description', label: 'Description',   col: catalogColMap.description },
                          { key: 'title',       label: 'Product Title', col: catalogColMap.title       },
                          { key: 'price',       label: 'Price (RRP)',   col: catalogColMap.price       },
                          { key: 'brand',       label: 'Brand',         col: catalogColMap.brand       },
                          { key: 'category',    label: 'Category',      col: catalogColMap.category    },
                        ] as const).map(({ key, label, col }) => {
                          const active = updateFields.has(key);
                          const disabled = !col;
                          return (
                            <button
                              key={key}
                              onClick={() => {
                                if (disabled) return;
                                setUpdateFields(prev => {
                                  const next = new Set(prev);
                                  if (next.has(key)) next.delete(key); else next.add(key);
                                  return next;
                                });
                              }}
                              disabled={disabled}
                              title={disabled ? `No column mapped for ${label}` : undefined}
                              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs border transition-all ${
                                disabled
                                  ? 'opacity-30 cursor-not-allowed border-border text-muted-foreground'
                                  : active
                                    ? 'bg-primary/15 border-primary/40 text-primary'
                                    : 'bg-muted/50 border-border text-muted-foreground hover:text-foreground hover:border-border/80'
                              }`}
                            >
                              <span className={`w-3 h-3 rounded-sm border flex items-center justify-center shrink-0 ${active && !disabled ? 'bg-primary border-primary' : 'border-muted-foreground/40'}`}>
                                {active && !disabled && <svg viewBox="0 0 10 10" fill="none" className="w-2.5 h-2.5"><path d="M2 5l2.5 2.5L8 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                              </span>
                              {label}
                              {col && <span className="text-[10px] text-slate-600 font-mono">({col})</span>}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Update action row */}
                    <div className="flex items-center gap-3 flex-wrap">
                      <button
                        onClick={handleUpdateListings}
                        disabled={updatingListings || updateFields.size === 0 || !catalogImportStoreId}
                        className="flex items-center gap-2 px-5 py-2 rounded-xl bg-sky-500/10 hover:bg-sky-500/15 border border-sky-500/20 text-sm text-sky-400 hover:text-sky-300 transition-all disabled:opacity-40"
                      >
                        {updatingListings
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>}
                        {updatingListings
                          ? updateProgress?.phase === 'building_map'
                            ? 'Building SKU map…'
                            : `Updating… ${updateProgress ? `${updateProgress.current} / ${updateProgress.total}` : ''}`
                          : `Update ${catalogSelected.size > 0 ? catalogSelected.size.toLocaleString() : catalogRows.length.toLocaleString()} listings`}
                      </button>
                      {!catalogImportStoreId && (
                        <span className="text-[11px] text-amber-400/70">Select a target store above</span>
                      )}
                    </div>

                    {/* Update progress bar */}
                    {updatingListings && updateProgress && updateProgress.total > 0 && (
                      <div className="space-y-1">
                        <div className="h-1.5 w-full bg-muted/50 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-sky-500/70 rounded-full transition-all duration-300"
                            style={{ width: `${(updateProgress.current / updateProgress.total) * 100}%` }}
                          />
                        </div>
                        <p className="text-[11px] text-slate-600 text-right">
                          {updateProgress.current.toLocaleString()} / {updateProgress.total.toLocaleString()} products
                        </p>
                      </div>
                    )}

                    {/* Update result */}
                    {updateResult && (
                      <div className={`flex items-start gap-3 p-4 rounded-xl border ${
                        updateResult.errorCount === 0
                          ? 'bg-sky-500/10 border-sky-500/20'
                          : 'bg-amber-500/10 border-amber-500/20'
                      }`}>
                        <CheckCircle2 className={`w-5 h-5 flex-shrink-0 mt-0.5 ${updateResult.errorCount === 0 ? 'text-sky-400' : 'text-amber-400'}`} />
                        <div className="space-y-1 min-w-0">
                          <p className="text-sm font-medium text-foreground">Update complete</p>
                          <p className="text-xs text-muted-foreground">
                            {updateResult.updatedCount} updated &middot; {updateResult.notFoundCount} not matched &middot; {updateResult.errorCount} failed
                          </p>
                          {updateResult.errors.length > 0 && (
                            <div className="mt-2 space-y-1 max-h-28 overflow-y-auto">
                              {updateResult.errors.map((e, i) => (
                                <p key={i} className="text-[11px] text-red-400 font-mono truncate" title={`${e.title}: ${e.error}`}>
                                  {e.title}: {e.error}
                                </p>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  </>
                )}
              </div>
            )}
          </div>
        )}

        {section === 'sync' && (<>
        {/* ── Danger zone ── */}
        {status?.configured && (
          <div className="glass-panel rounded-xl p-5 border border-red-500/10 space-y-3">
            <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Danger Zone</h2>
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm text-foreground">Remove Off Road Imports credentials</p>
                <p className="text-xs text-muted-foreground mt-0.5">Scheduled syncs will stop until you reconnect.</p>
              </div>
              <button onClick={handleDelete} disabled={deleting}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 text-sm text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-all whitespace-nowrap disabled:opacity-40">
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Disconnect
              </button>
            </div>
          </div>
        )}
        </>)}

      </div>
    </Layout>
  );
}
